import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, OnInit } from "@angular/core";
import { Project } from "@src/app/models/project";
import { Projectperson } from "@src/app/models/projectperson";
import { environment } from "@src/environments/environment";
import {
  BehaviorSubject,
  Observable,
  Subscribable,
  Subscription,
  firstValueFrom,
  lastValueFrom,
  of,
} from "rxjs";
import { AuthorizationService } from "../authorization.service";
import { SessionHelper } from "../sessionHelper";
import { Task } from "@src/app/models/task";
import { ImagestoreService } from "../imagestore.service";
import { Tick } from "@src/app/models/tick";
import { PersonOrganization } from "@src/app/models/personorganization";
import { Projectmember } from "@src/app/models/projectmember";
import { CustomerUser } from "@src/app/models/customeruser";
import { Orgsettings } from "@src/app/components/login/model/orgsettings";
import { UieventhubService } from "../uieventhub/uieventhub.service";
import { Customer } from "@src/app/models/customer";
import { Role } from "@src/app/models/role";
import { ETNotification } from "@src/app/models/ETnotification";
import { forkJoin } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { catchError } from "rxjs/operators";
import { Tag } from "@src/app/models/tag";
import { Filtertag } from "@src/app/models/filtertag";
import { untilDestroyed } from "@ngneat/until-destroy";
import { Auth } from "@src/app/models/auth";
import { FormTemplate } from "@src/app/components/forms/models/form.model";

@Injectable({
  providedIn: "root",
})
export class DatastoreService {
  lastgetdata: string;
  running = false;
  personprojList: Projectperson[];
  blockLoad = false;
  isFirstrun = true;
  currentproject: string;
  public auth = new BehaviorSubject<Auth[]>(null);
  public projectList = new BehaviorSubject<Project[]>([]);
  public taskList = new BehaviorSubject<Task[]>([]);
  public lightTaskList = new BehaviorSubject<Task[]>([]);
  public projectpersonList = new BehaviorSubject<Projectperson[]>([]);
  public orgMembers = new BehaviorSubject<PersonOrganization[]>([]);
  public customersUsers = new BehaviorSubject<CustomerUser[]>([]);
  public customers = new BehaviorSubject<Customer[]>([]);
  public roles = new BehaviorSubject<Role[]>([]);
  public notifications = new BehaviorSubject<ETNotification[]>([]);
  public filtertagList = new BehaviorSubject<Filtertag[]>([]);
  public tags = new BehaviorSubject<Tag[]>([]);
  public customerTasks = new BehaviorSubject<Task[]>([]);
  public publishedForms = new BehaviorSubject<FormTemplate[]>([]);
  public adhocTaskList = new BehaviorSubject<Task[]>([]);

  public auth$ = this.auth.asObservable();
  public projectList$ = this.projectList.asObservable();
  public taskList$ = this.taskList.asObservable();
  public lightTaskList$ = this.lightTaskList.asObservable();
  public projectpersonList$ = this.projectpersonList.asObservable();
  public orgMembers$ = this.orgMembers.asObservable();
  public customersUsers$ = this.customersUsers.asObservable();
  public customers$ = this.customers.asObservable();
  public roles$ = this.roles.asObservable();
  public notifications$ = this.notifications.asObservable();
  public filtertagList$ = this.filtertagList.asObservable();
  public tags$ = this.tags.asObservable();
  public customerTasks$ = this.customerTasks.asObservable();
  public publishedForms$ = this.publishedForms.asObservable();
  public adhocTaskList$ = this.adhocTaskList.asObservable();
  loaddatatimeout: NodeJS.Timeout;

  constructor(private http: HttpClient, private uievent: UieventhubService) {
    //console.log("data store Init");
    this.uievent.setShowLoader(true);
    this.load();
  }

  private extractNotificationIdFromTickKey(updatedsk1: string): string {
    if (!updatedsk1) {
      return '';
    }

    const parts = updatedsk1.split('_');
    return parts.length > 1 ? parts[1] : '';
  }

  private safeLoad<T>(observable: Observable<T>, fallbackValue: T, sourceName: string): Observable<T> {
    return observable.pipe(
      catchError((error) => {
        console.error(`Datastore load fallback for ${sourceName}:`, error);
        return of(fallbackValue);
      })
    );
  }

  private isCustomerSession(): boolean {
    return Boolean(SessionHelper.getCustomer());
  }

  private getSessionCustomer(): Customer | null {
    const serializedCustomer = SessionHelper.getCustomer();
    if (!serializedCustomer) {
      return null;
    }

    try {
      return JSON.parse(serializedCustomer) as Customer;
    } catch (_error) {
      return null;
    }
  }

  private getSessionOrgList(): PersonOrganization[] {
    const serializedOrgs = SessionHelper.getPersonOrgs();
    if (!serializedOrgs) {
      return [];
    }

    try {
      const parsed = JSON.parse(serializedOrgs);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (_error) {
      return [];
    }
  }
  //GET /organization/{organizationId}/person/{personId}/auth
  getAuthPerson() {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
    return this.http.get<Auth[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/person/" +
        SessionHelper.getUserSub() +
        "/auth",
      httpOptions
    );
  }
  // /organization/{organizationId}/project/list
  getProjects() {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Project[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/project/list",
      httpOptions
    );
  }

  // GET /organization/{organizationId}/tasks/lightweight
  getLightweightTasks() {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Task[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/tasks/lightweight",
      httpOptions
    ).pipe(
      map(tasks => this.backfillTaskData(tasks))
    );
  }

  // GET /organization/{organizationId}/tasks/adhoc
  getAdhocTasks() {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Task[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/tasks/adhoc",
      httpOptions
    );
  }

  // /project/list/task/list
  getTasks(projectId) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Task[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/project/" +
        projectId +
        "/task/list/",
      httpOptions
    ).pipe(
      map(tasks => this.backfillTaskData(tasks))
    );
  }

  getAllCustomerTasks(customerId: string) {
    const normalizedCustomerId = customerId ? customerId.split("_")[0] : "";
    if (!normalizedCustomerId) {
      return of([] as Task[]);
    }

    const pit = new Date().toISOString();
    return this.getCustomerProjects(pit).pipe(
      switchMap((projects) => {
        if (!Array.isArray(projects) || projects.length === 0) {
          return of([] as Task[]);
        }

        const taskRequests = projects.map((project) => this.getProjectTasks(project.projectId, pit));
        return forkJoin(taskRequests).pipe(
          map((taskResults) => this.backfillTaskData(taskResults.flat()))
        );
      }),
      catchError((_error) => of([] as Task[]))
    );
  }

  getRoles(project: Project) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
    return this.http.get<Role[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/project/" +
        project.projectId +
        "/role/list",
      httpOptions
    );
  }

  // getCustomerTasks() {
  //   const httpOptions = {
  //     headers: new HttpHeaders({
  //       "Content-Type": "application/json",
  //     }),
  //   };
  //   return this.http.get<Task[]>(
  //     environment.apiUrl +
  //       "/organization/" +
  //       SessionHelper.getChoosenOrg() +
  //       "/customer/" +
  //       JSON.parse(SessionHelper.getCustomer()).customerId.split("_")[0] +
  //       "/project/list/task/list/",
  //     httpOptions
  //   );
  // }
  getCustomerProjects(pit: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Project[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/customer/" +
        JSON.parse(SessionHelper.getCustomer()).customerId.split("_")[0] +
        "/project/list/pit/" +
        pit,
      httpOptions
    );
  }
  // get all the projects per person ()
  // /projectperson/list/person/{personId}/organization/{organizationId}
  getPersonProjects(personId: string) {
    // getPersonProjects(personId:string){
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Projectperson[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/projectperson/list/person/" +
        personId,
      httpOptions
    );
  }
  getProject(projectId: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Project>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/project/" +
        projectId,
      httpOptions
    );
  }
  getProjectTasks(projectId: string, pit: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Task[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/project/" +
        projectId +
        "/task/list",
      httpOptions
    );
  }

  //Get the projectperson list
  getProjectPersons(projectId) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    //GET	    /organization/{organizationId}/projectperson/{projectId}/person/list
    return this.http.get<Projectperson[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/projectperson/" +
        projectId +
        "/person/list",
      httpOptions
    );
  }
  //Get the Person Organization list
  getPersonOrganizations() {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
    return this.http.get<PersonOrganization[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/members",
      httpOptions
    );
  }
  //get customers
  getCustomers(): Observable<Customer[]> {
    const baseUrl =
      environment.apiUrl +
      "/organization/" +
      SessionHelper.getChoosenOrg() +
      "/customer/list/pit/" +
      new Date().toISOString();
  
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
  
    const allItems: Customer[] = [];
  
    const fetchPage = (exclusiveStartKey?: any): Observable<Customer[]> => {
      let url = baseUrl;
      if (exclusiveStartKey) {
        // Append ExclusiveStartKey as a query param (assume it's JSON-stringified)
        url += `?exclusiveStartKey=${encodeURIComponent(JSON.stringify(exclusiveStartKey))}`;
      }
  
      return this.http.get<any>(url, httpOptions).pipe(
        switchMap((res) => {
          const items: Customer[] = Array.isArray(res)
            ? res
            : (Array.isArray(res?.Items) ? res.Items : []);
          allItems.push(...items);
  
          const lastEvaluatedKey = Array.isArray(res) ? null : res?.LastEvaluatedKey;
          if (lastEvaluatedKey) {
            return fetchPage(lastEvaluatedKey);
          } else {
            return of(allItems);
          }
        })
      );
    };
  
    return fetchPage();
  }
  
  //Get the customer users list
  getCustomersUsers(customerId: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
    var pit = new Date().toISOString();
    ///organization/{organizationId}/customer/{customerId}/user/list/pit/{pitdate}
    return this.http.get<CustomerUser[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/customer/" +
        customerId +
        "/user/list/pit/" +
        pit,
      httpOptions
    );
  }

  getNotifications(date: Date) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
    // GET /notification/{personId}/date/{date}
    return this.http.get<ETNotification[]>(
      environment.apiUrl +
        "/notification/" +
        SessionHelper.getUserSub() +
        "/date/" +
        date.toISOString(),
      httpOptions
    );
  }

  //GET	    /person/{personId}/tagfilter/list/type/{tagtype}
  getFilterTags() {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
    return this.http.get<Filtertag[]>(
      environment.apiUrl +
        "/person/" +
        SessionHelper.getUserSub() +
        "/tagfilter/list",
      httpOptions
    );
  }

  //GET	    /organization/{organizationId}/tag/list/type/{tagtype}
  getTags() {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return this.http.get<Tag[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/tag/list",
      httpOptions
    );
  }

  // public setNotificationList(notifications:ETNotification[]){
  //   this.notifications.next(notifications);
  // }

  //function to postpone the loading of the data for 10 seconds
  postponeLoad() {
    this.blockLoad = true;
    setTimeout(() => {
      this.blockLoad = false;
    }, 20000);
  }

  async load() {
    if (this.blockLoad || !SessionHelper.getChoosenOrg()) {
      return;
    }
    if (this.isFirstrun == true) {
      this.uievent.addLoader("Loading data");
      this.blockLoad = true;

      const choosenOrg = SessionHelper.getChoosenOrg();
      let datetwomonthsago = new Date();
      datetwomonthsago.setDate(datetwomonthsago.getDate() - 7);
      const isCustomerSession = this.isCustomerSession();
      const sessionCustomer = this.getSessionCustomer();
      const sessionCustomerId = sessionCustomer && sessionCustomer.customerId
        ? sessionCustomer.customerId.split("_")[0]
        : "";

      // Parallelize fetching projects, orgMembers, customers
      forkJoin({
        auth: this.safeLoad(this.getAuthPerson(), [], "auth"),
        projects: this.safeLoad(
          isCustomerSession
            ? this.getCustomerProjects(new Date().toISOString())
            : this.getProjects(),
          [],
          "projects"
        ),
        orgMembers: this.safeLoad(
          isCustomerSession ? of(this.getSessionOrgList()) : this.getPersonOrganizations(),
          [],
          "orgMembers"
        ),
        customers: this.safeLoad(
          isCustomerSession ? of(sessionCustomer ? [sessionCustomer] : []) : this.getCustomers(),
          [],
          "customers"
        ),
        notifications: this.safeLoad(this.getNotifications(datetwomonthsago), [], "notifications"),
        personProjects: this.safeLoad(
          isCustomerSession ? of([]) : this.getPersonProjects(SessionHelper.getUserSub()),
          [],
          "personProjects"
        ),
        filtertagList: this.safeLoad(isCustomerSession ? of([]) : this.getFilterTags(), [], "filtertagList"),
        tags: this.safeLoad(isCustomerSession ? of([]) : this.getTags(), [], "tags"),
        customerTasks: this.safeLoad(
          this.getAllCustomerTasks(sessionCustomerId),
          [],
          "customerTasks"
        ),
        lightweightTasks: this.safeLoad(this.getLightweightTasks(), [], "lightweightTasks"),
      }).subscribe({
        next: ({
          auth,
          projects,
          orgMembers,
          customers,
          notifications,
          personProjects,
          filtertagList,
          tags,
          customerTasks,
          lightweightTasks,
        }) => {
          //console.log("auth", auth);
          SessionHelper.setPersonAuth(JSON.stringify(auth));
          this.auth.next(auth);

          // Backfill missing fields for backward compatibility
          projects = this.backfillProjectData(projects);
          orgMembers = this.backfillPersonData(orgMembers);

          SessionHelper.setPortalProjectList(JSON.stringify(projects));
          //console.log("projects", projects);
          this.projectList.next(projects);

          SessionHelper.setPortalOrgMembers(JSON.stringify(orgMembers));
          this.orgMembers.next(orgMembers);

         //console.log("customers", JSON.stringify(customers));
          SessionHelper.setCustomers(JSON.stringify(customers));
          this.customers.next(customers);

          this.projectpersonList.next(personProjects);
          this.notifications.next(notifications);

          if (!isCustomerSession) {
            this.loadCustomerUsers(customers);
          } else {
            this.customersUsers.next([]);
          }

          this.filtertagList.next(filtertagList);
          this.tags.next(tags);

         //console.log("customerTasks", customerTasks);
          this.customerTasks.next(customerTasks);

          //console.log("lightweightTasks", lightweightTasks);
          this.lightTaskList.next(lightweightTasks);

          if (SessionHelper.getCustomer()) {
            SessionHelper.setPortalTaskList(JSON.stringify(customerTasks));
            this.taskList.next(customerTasks);
          }

          this.uievent.removeLoader("Loading data");
          this.blockLoad = false;
          this.isFirstrun = false;
        },
        error: (error) => {
          console.error("Error loading data:", error);
        },
        complete: () => {
          this.uievent.removeLoader("Loading data");
          this.blockLoad = false;
          this.isFirstrun = false;
        },
      });
    } else {
      //console.log("customersUsers", this.customersUsers.getValue());
      //console.log("get ticker data");
      this.getTickerData().subscribe((data) => {
        let auth = this.auth.getValue();
        let authHasChanges = false;
        let projects = this.projectList.getValue();
        let projectHasChanges = false;
        let tasks = SessionHelper.getCustomer()
          ? (this.customerTasks.getValue() ?? [])
          : (this.taskList.getValue() ?? []);
        let taskHasChanges = false;
        let projectperson = this.projectpersonList.getValue();
        let projectpersonHasChanges = false;
        let orgMembers = this.orgMembers.getValue();
        let orgMembersHasChanges = false;
        let customers = this.customers.getValue();
        let customersHasChanges = false;
        let customersUsers = this.customersUsers.getValue();
        let customersUsersHasChanges = false;
        let roles = this.roles.getValue();
        let rolesHasChanges = false;
        let notifications = this.notifications.getValue();
        let notificationsHasChanges = false;
        let tags = this.tags.getValue();
        let tagsHasChanges = false;
        let filtertagList = this.filtertagList.getValue();
        let filtertagListHasChanges = false;
        //loop through the data and update the project list and the task list
        //console.log("ticker data", data);
        data.forEach((element) => {
          //console.log("element", element);
          var parsedBody = JSON.parse(element.body);
          // if the updatedpk is auth then update the auth list
          if (element.updatedpk.startsWith( "auth" )) {
            //TODO check if the auth is deleted. No current way to do this in the datastore
           //console.log("updated auth", parsedBody);
            authHasChanges = true;
            var updatedauth: Auth = {
              personId: element.updatedpk.split("_")[2],
              organizationId: element.updatedpk.split("_")[1],
              entityId: element.updatedsk1.split("_")[1],
              type: element.updatedsk1.split("_")[0],
            };
            //find out if the auth exists in the auth list if not add it
            if (auth.findIndex((x) => x.entityId == updatedauth.entityId && x.type == updatedauth.type) == -1) {
              //console.log("add auth", updatedauth);
              auth.push(updatedauth);
            }
          }

          //if the updatedpk is project then update the project list
          if (element.updatedpk =="project") {
            //console.log("updated project", parsedBody);
            projectHasChanges = true;
            //handle the deletion of a project
            if (parsedBody.date_removed && parsedBody.date_removed.S != "") {
              //console.log("delete project", element.updatedsk2);
              projects = projects.filter(
                (x) => x.projectId != element.updatedsk1.split("_")[1]
              );
            } else {
              var updatedproject: Project = {
                projectId: element.updatedsk1.split("_")[1],
                organizationId: parsedBody.organizationId
                  ? parsedBody.organizationId.S
                  : "",
                organizationname: parsedBody.organizationname
                  ? parsedBody.organizationname.S
                  : "",
                orgdesc: parsedBody.orgdesc ? parsedBody.orgdesc.S : "",
                orgemail: parsedBody.orgemail ? parsedBody.orgemail.S : "",
                orgwww: parsedBody.orgwww ? parsedBody.orgwww.S : "",
                orglogourl: parsedBody.orglogourl
                  ? parsedBody.orglogourl.S
                  : "",
                fullname: parsedBody.fullname ? parsedBody.fullname.S : "",
                title: parsedBody.title ? parsedBody.title.S : "",
                subtitle: parsedBody.subtitle.S,
                shortdescription: parsedBody.shortdescription
                  ? parsedBody.shortdescription.S
                  : "",
                projectdescription: parsedBody.projectdescription
                  ? parsedBody.projectdescription.S
                  : "",
                projectscope: parsedBody.projectscope
                  ? parsedBody.projectscope.S
                  : "",
                projectlocation: parsedBody.projectlocation
                  ? parsedBody.projectlocation.S
                  : "",
                creator: parsedBody.creator ? parsedBody.creator.S : "",
                created: new Date(parsedBody.created.S),
                customer: parsedBody.customer ? parsedBody.customer.S : "",
                customername: parsedBody.customername
                  ? parsedBody.customername.S
                  : "",
                start_date: parsedBody.start_date
                  ? new Date(parsedBody.start_date.S)
                  : null,
                end_date: parsedBody.end_date
                  ? new Date(parsedBody.end_date.S)
                  : null,
                templateId: parsedBody.templateId
                  ? parsedBody.templateId.S
                  : "",
                isactive: parsedBody.isactive.BOOL,
                canedit: parsedBody.canedit ? parsedBody.canedit.BOOL : false,
                overduedays: parsedBody.overduedays
                  ? parsedBody.overduedays.S
                  : "0",
                progress: parsedBody.progress ? parsedBody.progress.S : "0",
                dealId: parsedBody.dealId ? parsedBody.dealId.S : "",
                dealcrmId: parsedBody.dealcrmId ? parsedBody.dealcrmId.S : "",
                lastupdated: parsedBody.lastupdated.N
                  ? parseInt(parsedBody.lastupdated.N)
                  : 0,
                workload_fte: parsedBody.workload_fte
                  ? parseFloat(parsedBody.workload_fte.N)
                  : 1,
                completed_date: parsedBody.completed_date
                  ? parsedBody.completed_date.S
                  : null,
                // lastupdated: parsedBody.lastupdated.S
                // ? new Date(parsedBody.lastupdated.S).getTime()
                // : new Date(0).getTime(),
              };

              //console.log("project updated datastore", updatedproject);
              //find out if the project exists in the project list if not add it
              if (
                projects.findIndex(
                  (x) => x.projectId == element.updatedsk1.split("_")[1]
                ) == -1
              ) {
                //todo parse the data to right format. No S: in the data and projectid instaed of updatedsk1

                //console.log('add project',updatedproject);
                projects.push(updatedproject);
              } else {
                //update the project list with the new data
                //console.log('update project',updatedproject);

                //TODO only update if the update is newer than the current data. Might have to add add a lastupdated date to the projects
                let i = projects.findIndex(
                  (x) => x.projectId == updatedproject.projectId
                );

                if (
                  !projects[i].lastupdated ||
                  projects[i].lastupdated.toString().includes("-") ||
                  projects[i].lastupdated < updatedproject.lastupdated
                ) {
                  projects[i] = updatedproject;
                }
              }
            }
          }
          //if the updatedpk starts with projectrole then update the role list
          if (element.updatedpk.startsWith("projectrole")) {
            //console.log("updated role", parsedBody);

            rolesHasChanges = true;
            var updatedrole: Role = {
              roleId: element.updatedsk1.split("_")[1],
              projectrole: parsedBody.projectrole.S,
              projectId: element.updatedpk.split("_")[1],
              rolelocalid: parsedBody.rolelocalid.S,
              assignedperson: parsedBody.assignedperson
                ? parsedBody.assignedperson.S
                : "",
              organizationId: parsedBody.organizationId.S,
            };
            //find out if the role exists in the role list if not add it
            if (
              roles.findIndex(
                (x) => x.roleId == element.updatedsk1.split("_")[1]
              ) == -1
            ) {
              //console.log("add role", updatedrole);
              roles.push(updatedrole);
            } else {
              //update the role list with the new data
              //only update if the update is newer than the current data.
              var i = roles.findIndex(
                (x) => x.roleId == element.updatedsk1.split("_")[1]
              );
              //console.log("update role", updatedrole);
              roles[i] = updatedrole;
            }
          }

          //if the updatedpk starts with projecttask then update the task list
          if (element.updatedpk.startsWith("projecttask")) {
           //console.log("updated task", parsedBody);
           //console.log("Task list before update:", tasks);
            //check if the task is deleted
            if (
              parsedBody.validfrom.S == parsedBody.validto.S ||
              (parsedBody.taskvisibility.S == "vendor_only" &&
                SessionHelper.getCustomer())
            ) {
              //console.log("delete task", parsedBody);
              tasks = tasks.filter(
                (x) =>
                  x.taskId.split("_")[0] != element.updatedsk1.split("_")[1]
              );
              taskHasChanges = true;
              //skip historic data
            } else if (parsedBody.validto.S == "9999-01-01T00:00:00.000Z") {
             //console.log("update task route");
              taskHasChanges = true;
              const existingTaskIndex = tasks.findIndex(
                (x) => x.taskId.split("_")[0] == element.updatedsk1.split("_")[1]
              );
              const existingTask = existingTaskIndex > -1 ? tasks[existingTaskIndex] : null;
              var updatedtask: Task = {
                taskId:
                  element.updatedsk1.split("_")[1] +
                  "_" +
                  element.updatedsk1.split("_")[2],
                projectId: element.updatedpk.split("_")[1],
                organizationId: parsedBody.organizationId
                  ? parsedBody.organizationId.S
                  : "no-org-id",
                organizationname: parsedBody.organizationname
                  ? parsedBody.organizationname.S
                  : "",
                orgdesc: parsedBody.orgdesc ? parsedBody.orgdesc.S : "",
                orgemail: parsedBody.orgemail ? parsedBody.orgemail.S : "",
                orgwww: parsedBody.orgwww ? parsedBody.orgwww.S : "",
                orglogourl: parsedBody.orglogourl
                  ? parsedBody.orglogourl.S
                  : "",
                title: parsedBody.title ? parsedBody.title.S : "",
                creator: parsedBody.creator ? parsedBody.creator.S : "",
                created: parsedBody.created ? parsedBody.created.S : "",
                customer: parsedBody.customer ? parsedBody.customer.S : "",
                customername: parsedBody.customername
                  ? parsedBody.customername.S
                  : "",
                startdate: parsedBody.startdate ? parsedBody.startdate.S : "",
                duedate: parsedBody.duedate ? parsedBody.duedate.S : "",
                description: parsedBody.description
                  ? parsedBody.description.S
                  : "",
                taskstate: parsedBody.taskstate ? parsedBody.taskstate.S : "",
                responsible: parsedBody.responsible
                  ? parsedBody.responsible.S
                  : (existingTask?.responsible || (parsedBody.creator ? parsedBody.creator.S : "")),
                stageId: parsedBody.stageId ? parsedBody.stageId.S : "",
                stagename: parsedBody.stagename ? parsedBody.stagename.S : "",
                versioncreator: parsedBody.versioncreator
                  ? parsedBody.versioncreator.S
                  : "",
                validfrom: parsedBody.validfrom ? parsedBody.validfrom.S : "",
                validto: parsedBody.validto ? parsedBody.validto.S : "",
                taskvisibility: parsedBody.taskvisibility
                  ? parsedBody.taskvisibility.S
                  : "",
                projectname: parsedBody.projectname
                  ? parsedBody.projectname.S
                  : "",
                projectdescription: parsedBody.projectdescription
                  ? parsedBody.projectdescription.S
                  : "",
                show: parsedBody.show ? parsedBody.show.BOOL : false,
                canEdit: parsedBody.canEdit ? parsedBody.canEdit.BOOL : false,
                sortOrder: parsedBody.sortOrder ? parseInt(parsedBody.sortOrder.S) || 0 : 0,
                numberoffiles: parsedBody.numberoffiles?.N
                  ? parsedBody.numberoffiles.N
                  : parsedBody.numberoffiles?.S ? parsedBody.numberoffiles.S :"0",
                tasktype: parsedBody.tasktype ? parsedBody.tasktype.S : (existingTask?.tasktype || "task"),
                linktext: parsedBody.linktext ? parsedBody.linktext.S : "",
                linkurl: parsedBody.linkurl ? parsedBody.linkurl.S : "",
                uploadFields: parsedBody.uploadFields
                  ? parsedBody.uploadFields.L.map((x) => x.S)
                  : [''],
                // Keep existing form linkage when ticker updates do not include form metadata.
                formTemplateId: parsedBody.formTemplateId
                  ? parsedBody.formTemplateId.S
                  : parsedBody.form_template_id
                    ? parsedBody.form_template_id.S
                    : existingTask?.formTemplateId,
                formTemplateVersion: parsedBody.formTemplateVersion
                  ? parseInt(parsedBody.formTemplateVersion.N || parsedBody.formTemplateVersion.S, 10)
                  : parsedBody.form_template_version
                    ? parseInt(parsedBody.form_template_version.N || parsedBody.form_template_version.S, 10)
                    : existingTask?.formTemplateVersion,
                formTemplateName: parsedBody.formTemplateName
                  ? parsedBody.formTemplateName.S
                  : parsedBody.form_template_name
                    ? parsedBody.form_template_name.S
                    : existingTask?.formTemplateName,
              };
              //find out if the task exists in the task list if not add it
              if (existingTaskIndex == -1) {
                //console.log("add task", updatedtask);
                tasks.push(updatedtask);
              } else {
                //update the task list with the new data
                //only update if the update is newer than the current data.
                //console.log("update task", updatedtask);
                var i = existingTaskIndex;
                if (
                  new Date(tasks[i].validfrom) < new Date(updatedtask.validfrom)
                ) {
                  
                  tasks[i] = updatedtask;
                }
              }
            }
          }
          //if the updatedpk starts with projectperson then update the projectperson list
          if (element.updatedpk.startsWith("projectperson")) {
            //console.log("updated projectperson", element);
            //check if the projectperson is deleted
            if (parsedBody.date_removed && parsedBody.date_removed.S != "") {
              projectperson = projectperson.filter(
                (x) =>
                  x.projectId != element.updatedsk1.split("_")[1] ||
                  x.personId != element.updatedpk.split("_")[1]
              );
              projectpersonHasChanges = true;
              //skip historic data
            } else {
              projectpersonHasChanges = true;

              var updatedprojectperson: Projectperson = {
                projectId: element.updatedsk1.split("_")[1],
                personId: element.updatedpk.split("_")[1],
                organization: parsedBody.organization
                  ? parsedBody.organization.S
                  : "",
                fullname: parsedBody.fullname ? parsedBody.fullname.S : "",
                email: parsedBody.email ? parsedBody.email.S : "",
                phone: parsedBody.phone ? parsedBody.phone.S : "",
                im: parsedBody.im ? parsedBody.im.S : "",
                projectrole: parsedBody.projectrole
                  ? parsedBody.projectrole.S
                  : "",
                start_date: parsedBody.start_date
                  ? parsedBody.start_date.S
                  : "",
                end_date: parsedBody.end_date ? parsedBody.end_date.S : "",
                title: parsedBody.title ? parsedBody.title.S : "",
                subtitle: parsedBody.subtitle ? parsedBody.subtitle.S : "",
                shortdescription: parsedBody.shortdescription
                  ? parsedBody.shortdescription.S
                  : "",
                projectdescription: parsedBody.projectdescription
                  ? parsedBody.projectdescription.S
                  : "",
                projectscope: parsedBody.projectscope
                  ? parsedBody.projectscope.S
                  : "",
                projectlocation: parsedBody.projectlocation
                  ? parsedBody.projectlocation.S
                  : "",
                isactive: true,
                lastupdated:
                  parsedBody.lastupdated && parsedBody.lastupdated.N
                    ? parseInt(parsedBody.lastupdated.N)
                    : 0,
                iscustomer: parsedBody.iscustomer
                  ? parsedBody.iscustomer.BOOL
                  : false,
              };
              //find out if the projectperson exists in the projectperson list if not add it
              if (
                projectperson.findIndex(
                  (x) =>
                    x.projectId == element.updatedsk1.split("_")[1] &&
                    x.personId == element.updatedpk.split("_")[1]
                ) == -1
              ) {
                //console.log("add projectperson", updatedprojectperson);
                projectperson.push(updatedprojectperson);
              } else {
                //update the projectperson list with the new data
                //only update if the update is newer than the current data.
                var i = projectperson.findIndex(
                  (x) =>
                    x.projectId == element.updatedsk1.split("_")[1] &&
                    x.personId == element.updatedpk.split("_")[1]
                );
                if (
                  projectperson[i].lastupdated <
                  updatedprojectperson.lastupdated
                ) {
                  //console.log("update projectperson", updatedprojectperson);
                  projectperson[i] = updatedprojectperson;
                }
              }
            }
          }
          //if the updatedpk starts with personoranization then update the orgMembers list
          if (element.updatedpk.startsWith("personorganization")) {
            //console.log("updated personorganization", parsedBody);
            //check if the personorganization is deleted
            if (
              (parsedBody.date_removed && parsedBody.date_removed.S != "") ||
              (parsedBody.date_left && parsedBody.date_left.S != "")
            ) {
              orgMembers = orgMembers.filter(
                (x) => x.personId != element.updatedpk.split("_")[1]
              );
              orgMembersHasChanges = true;
              //skip historic data
            } else {
              orgMembersHasChanges = true;
              var updatedpersonorganization: PersonOrganization = {
                personId: element.updatedpk.split("_")[1],
                organizationId: element.updatedsk1.split("_")[1],
                organization: parsedBody.organization
                  ? parsedBody.organization.S
                  : "",
                www: parsedBody.www ? parsedBody.www.S : "",
                modules: parsedBody.modules ? parsedBody.modules.S : "",
                membername: parsedBody.membername.S,
                email: parsedBody.email.S,
                phone: parsedBody.phone.S,
                member_function: parsedBody.member_function.S,
                date_joined: parsedBody.date_joined
                  ? parsedBody.date_joined.S
                  : "",
              };
              //console.log("updated personorganization", element);
              //find out if the personorganization exists in the orgMembers list if not add it
              if (
                orgMembers.findIndex(
                  (x) =>
                    x.personId == element.updatedpk.split("_")[1] &&
                    x.organizationId == element.updatedsk1.split("_")[1]
                ) == -1
              ) {
                //console.log("add personorganization", updatedpersonorganization);
                orgMembers.push(updatedpersonorganization);
              } else {
                //update the orgMembers list with the new data
                //only update if the update is newer than the current data.
                var i = orgMembers.findIndex(
                  (x) =>
                    x.personId == element.updatedpk.split("_")[1] &&
                    x.organizationId == element.updatedsk1.split("_")[1]
                );
                //console.log("update personorganization", updatedpersonorganization);
                orgMembers[i] = updatedpersonorganization;
              }
            }
          }
          //if the updatedpk starts with customer then update the customers list
          if (element.updatedpk.startsWith("organizationcustomer_")) {
            //console.log("updated customer", parsedBody);
            //check if the customer is deleted
            if (
              parsedBody.validfrom &&
              parsedBody.validto &&
              parsedBody.validfrom.S == parsedBody.validto.S
            ) {
              //console.log('Delete customer',parsedBody);
              customers = customers.filter(
                (x) =>
                  x.customerId.split("_")[0] != element.updatedsk1.split("_")[1]
              );
              customersHasChanges = true;
              //skip historic data
            } else if(parsedBody.validto && parsedBody.validto.S != '9999-01-01T00:00:00.000Z'){
              //skip historic data
              //console.log('Skip historic customer data',parsedBody);
            }
              
              else{
              customersHasChanges = true;
              var updatedcustomer: Customer = {
                /**
                 *     customerId:string;
    title:string;
    description?:string;
    website?:string;
    address1?:string;
    address2?:string;
    zip?:string;
    city?:string;
    main_contact?:string;
    logo?:string;
    creator:string;
    created:string;
    versioncreator:string;
    validfrom:string;
    validto:string;
    organizationId:string;
    customercrmId:string;
                 */

                customerId: element.updatedsk1.split("_")[1],
                organizationId: parsedBody.organizationId
                  ? parsedBody.organizationId.S
                  : "",
                title: parsedBody.title ? parsedBody.title.S : "",
                description: parsedBody.description
                  ? parsedBody.description.S
                  : "",
                website: parsedBody.website ? parsedBody.website.S : "",
                address1: parsedBody.address1 ? parsedBody.address1.S : "",
                address2: parsedBody.address2 ? parsedBody.address2.S : "",
                zip: parsedBody.zip ? parsedBody.zip.S : "",
                city: parsedBody.city ? parsedBody.city.S : "",
                main_contact: parsedBody.main_contact
                  ? parsedBody.main_contact.S
                  : "",
                logo: parsedBody.logo ? parsedBody.logo.S : "",
                creator: parsedBody.creator ? parsedBody.creator.S : "",
                created: new Date(parsedBody.created.S).toISOString(),
                versioncreator: parsedBody.versioncreator
                  ? parsedBody.versioncreator.S
                  : "",
                validfrom: parsedBody.validfrom ? parsedBody.validfrom.S : "",
                validto: parsedBody.validto ? parsedBody.validto.S : "",
                customercrmId: parsedBody.customercrmId
                  ? parsedBody.customercrmId.S
                  : "",
                customerType: parsedBody.customerType
                  ? parsedBody.customerType.S
                  : "",  
              };
              //find out if the customer exists in the customers list if not add it
              if (
                customers.findIndex(
                  (x) => x.customerId == element.updatedsk1.split("_")[1]
                ) == -1
              ) {
                //console.log("add customer", updatedcustomer);
                customers.push(updatedcustomer);
              } else {
                //update the customers list with the new data
                //only update if the update is newer than the current data.
                var i = customers.findIndex(
                  (x) => x.customerId == element.updatedsk1.split("_")[1]
                );
                //console.log("update customer", updatedcustomer);
                customers[i] = updatedcustomer;
              }
            }
          }

          //if the updatedpk starts with customeruser then update the customersUsers list
          if (element.updatedpk.startsWith("customeruser")) {
            //console.log("updated customeruser", parsedBody);
            //check if the customeruser is deleted
            if (parsedBody.validfrom.S == parsedBody.validto.S) {
              customersUsers = customersUsers.filter(
                (x) => x.userId != element.updatedsk1.split("_")[1]
              );
              customersUsersHasChanges = true;
              //skip historic data
            } else {
              customersUsersHasChanges = true;
              var updatedcustomeruser: CustomerUser = {
                /*    fullname:string;
    username:string;
    jobtitle:string;    
    userrole:string;
    creator:string;
    created:string;
    email:string;
    phone?:string;
    userId:string;
    customerId:string;
    customerusercrmId:string;
    sendinvite:boolean;
                */
                userId: element.updatedsk1.split("_")[1],
                customerId: element.updatedpk.split("_")[1],
                fullname: parsedBody.fullname ? parsedBody.fullname.S : "",
                jobtitle: parsedBody.jobtitle ? parsedBody.jobtitle.S : "",
                userrole: parsedBody.userrole ? parsedBody.userrole.S : "",
                creator: parsedBody.creator ? parsedBody.creator.S : "",
                created: parsedBody.created ? parsedBody.created.S : "",
                email: parsedBody.email ? parsedBody.email.S : "",
                phone: parsedBody.phone ? parsedBody.phone.S : "",
                customerusercrmId: parsedBody.customerusercrmId
                  ? parsedBody.customerusercrmId.S
                  : "",
                sendinvite: parsedBody.sendinvite
                  ? parsedBody.sendinvite.BOOL
                  : false,
                isactive: parsedBody.isactive
                  ? parsedBody.isactive.BOOL
                  : false,
                username: parsedBody.username ? parsedBody.username.S : "",
                validto: parsedBody.validto
                  ? parsedBody.validto.S
                  : new Date("9999-01-01").toISOString(),
                organizationId: parsedBody.organizationId
                  ? parsedBody.organizationId.S
                  : "",
              };
              //find out if the customeruser exists in the customersUsers list if not add it
              
              if (
                customersUsers.findIndex(
                  (x) => x.userId.split("_")[1] == element.updatedsk1.split("_")[1]
                ) == -1
              ) {
                //console.log("add customeruser", updatedcustomeruser);
                customersUsers.push(updatedcustomeruser);
              } else {
                //update the customersUsers list with the new data
                //only update if the update is newer than the current data.
                var i = customersUsers.findIndex(
                  (x) => x.userId == element.updatedsk1.split("_")[1]
                );
                //console.log("update customeruser", updatedcustomeruser);
                customersUsers[i] = updatedcustomeruser;
              }
            }
          }

          // //if the updatedpk starts with notification then update the notifications list
          if (element.updatedpk.startsWith("notification")) {
            //console.log("updated notification", parsedBody);
            //check if the notification is deleted

            notificationsHasChanges = true;
            //console.log("notification", element.updatedsk1);
            const normalizedNotificationId = this.extractNotificationIdFromTickKey(element.updatedsk1);
            var updatednotification: ETNotification = {
              personId: element.updatedpk.split("_")[1],
              notificationId: normalizedNotificationId,
              notificationtitle: parsedBody.notificationtitle.S,
              notificationdescription: parsedBody.notificationdescription.S,
              notificationimage: parsedBody.notificationimage.S,
              datecreated: parsedBody.datecreated.S,
              dateread: parsedBody.dateread ? parsedBody.dateread.S : "",
              dateviewed: parsedBody.dateviewed ? parsedBody.dateviewed.S : "",
              link: parsedBody.link.S,
              organization: parsedBody.organization.S,
            };
            //find out if the notification exists in the notifications list if not add it
            if (
              notifications.findIndex(
                (x) => x.notificationId == normalizedNotificationId
              ) == -1
            ) {
              //console.log("add notification", updatednotification);
              notifications.push(updatednotification);
            } else {
              //update the notifications list with the new data
              //only update if the update is newer than the current data.
              var i = notifications.findIndex(
                (x) => x.notificationId == normalizedNotificationId
              );
              //console.log("update notification", updatednotification);
              notifications[i] = updatednotification;
            }
          }
          //if the updatedpk starts with tag then update the tags list
          if (element.updatedpk.startsWith("tag")) {
            //console.log("updated tag", parsedBody);
            tagsHasChanges = true;
            var updatedtag: Tag = {
              tagId: element.updatedsk1.split("_")[1],
              title: parsedBody.title.S,
              tagtype: parsedBody.tagtype.S,
              created: new Date(parsedBody.created.S).toISOString(),
              creator: parsedBody.creator.S,
              organizationId: parsedBody.organizationId.S,
              organizationname: parsedBody.organizationname.S,
            };
            //find out if the tag exists in the tags list if not add it
            if (
              tags.findIndex(
                (x) => x.tagId == element.updatedsk1.split("_")[1]
              ) == -1
            ) {
              //console.log("add tag", updatedtag);
              tags.push(updatedtag);
            } else {
              //update the tags list with the new data
              //only update if the update is newer than the current data.
              var i = tags.findIndex(
                (x) => x.tagId == element.updatedsk1.split("_")[1]
              );
              //console.log("update tag", updatedtag);
              tags[i] = updatedtag;
            }
          }
          //if the updatedpk starts with tagfilter then update the tagfilter list
          if (element.updatedpk.startsWith("tagfilter")) {
           //console.log("updated tagfilter", parsedBody);
            
            //delete the filtertag if it is removed
            if (parsedBody.date_removed && parsedBody.date_removed.S != "") {
              filtertagList = filtertagList.filter(
                (x) => x.tagId != element.updatedsk1.split("_")[1]
              );
              filtertagListHasChanges = true;
            } else {
              var updatedfiltertag: Filtertag = {
                tagId: element.updatedsk1.split("_")[1],
                title: parsedBody.title.S,
                tagtype: parsedBody.tagtype.S,
                created: new Date(parsedBody.created.S).toISOString(),
                creator: parsedBody.creator.S,
                organizationId: parsedBody.organizationId.S,
                organizationname: parsedBody.organizationname.S,
                personId: SessionHelper.getUserSub(),
              };

              //find out if the filtertag exists in the filtertag list if not add it
              if (
                filtertagList.findIndex(
                  (x) => x.tagId == element.updatedsk1.split("_")[1]
                ) == -1
              ) {
                //console.log("add filtertag", updatedfiltertag);
                filtertagList.push(updatedfiltertag);
                filtertagListHasChanges = true;
              } else {
                //update the filtertag list with the new data
                //only update if the update is newer than the current data.
                var i = filtertagList.findIndex(
                  (x) => x.tagId == element.updatedsk1.split("_")[1]
                );
                //console.log("update filtertag", updatedfiltertag);
                filtertagList[i] = updatedfiltertag;
                filtertagListHasChanges = true;
              }
            }
          }
        });
        if (authHasChanges) {
          SessionHelper.setPersonAuth(JSON.stringify(auth));
          this.auth.next(auth);
        }

        //update the project list with the new data
        if (projectHasChanges) {
          //console.log("update project list");
          SessionHelper.setPortalProjectList(JSON.stringify(projects));
          this.projectList.next(projects);
        }
        //update the role list with the new data
        if (rolesHasChanges) {
          SessionHelper.setRoles(JSON.stringify(roles));
          this.roles.next(roles);
        }

        //update the task list with the new data
        if (taskHasChanges) {
          // Sort tasks by sortOrder before updating
          tasks.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
          
          // keep SessionHelper cache in sync if you use it elsewhere
          SessionHelper.setPortalTaskList(JSON.stringify(tasks));

          if (SessionHelper.getCustomer()) {
            this.customerTasks.next(tasks);   // update customer view
            this.taskList.next(tasks);        // optional: keep both in sync
          } else {
            this.taskList.next(tasks);
          }
        }

        //update the projectperson list with the new data
        if (projectpersonHasChanges) {
          SessionHelper.setPortalProjectPersonList(
            JSON.stringify(projectperson)
          );
          this.projectpersonList.next(projectperson);
        }
        //update the orgMembers list with the new data
        if (orgMembersHasChanges) {
          SessionHelper.setPortalOrgMembers(JSON.stringify(orgMembers));
          this.orgMembers.next(orgMembers);
        }
        //update the customers list with the new data
        if (customersHasChanges) {
          SessionHelper.setCustomers(JSON.stringify(customers));
          this.customers.next(customers);
        }
        //update the customersUsers list with the new data
        if (customersUsersHasChanges) {
          SessionHelper.setCustomersUsers(JSON.stringify(customersUsers));
          //console.log("customersUsers", customersUsers);
          this.customersUsers.next(customersUsers);
        }
        //update the notifications list with the new data
        if (notificationsHasChanges) {
          this.notifications.next(notifications);
        }
        //update the tags list with the new data
        if (tagsHasChanges) {
          this.tags.next(tags);
        }
        //update the filtertag list with the new data
        if (filtertagListHasChanges) {
          this.filtertagList.next(filtertagList);
        }
      });
    } //end of blockload
  }

  public loadProjectDependentData(projects: Project[]) {
    //console.log("loadProjectDependentData", projects);
    if (projects.length > 0) {
      this.uievent.setProjectDataLoading(true);
      const taskRequests = projects.map((project) =>
        this.safeLoad(this.getTasks(project.projectId), [], `projectTasks:${project.projectId}`)
      );
      const roleRequests = projects.map((project) =>
        this.safeLoad(this.getRoles(project), [], `projectRoles:${project.projectId}`)
      );
      const projectPersonRequests = projects.map((project) =>
        this.safeLoad(this.getProjectPersons(project.projectId), [], `projectPersons:${project.projectId}`)
      );
      const customerUserRequests = projects.map((project) =>
        this.safeLoad(this.getCustomersUsers(project.customer), [], `customerUsers:${project.customer}`)
      );

      // Parallelize tasks, roles, projectPersons, and customerUsers fetching
      forkJoin({
        tasks: forkJoin(taskRequests).pipe(
          map((taskResults) => taskResults.flat())
        ),
        roles: forkJoin(roleRequests).pipe(
          map((roleResults) => roleResults.flat())
        ),
        projectPersons: forkJoin(projectPersonRequests).pipe(
          map((personResults) => personResults.flat())
        ),
        customerUsers: forkJoin(customerUserRequests).pipe(
          map((userResults) => userResults.flat())
        ),
      }).subscribe(
        ({ tasks, roles, projectPersons, customerUsers }) => {
          tasks = this.backfillTaskData(tasks);
          SessionHelper.setPortalTaskList(JSON.stringify(tasks));
          this.taskList.next(tasks);

          this.roles.next(roles);

          SessionHelper.setPortalProjectPersonList(
            JSON.stringify(projectPersons)
          );
          this.projectpersonList.next(projectPersons);
          //console.log("customerUsers", customerUsers);
          this.customersUsers.next(customerUsers);

          this.loaddatatimeout = setTimeout(() => {
            this.uievent.setProjectDataLoading(false);
          }, 3000);
        },
        (error) => console.error("Error loading project-dependent data:", error)
      );
    }
  }

  private loadCustomerUsers(customers: Customer[]) {
    if (customers.length > 0) {
      const customerUserRequests = customers.map((customer) =>
        this.getCustomersUsers(customer.customerId)
      );

      // Fetch customer users in parallel
      forkJoin(customerUserRequests).subscribe(
        (customerUsersResults) => {
          const allCustomerUsers = customerUsersResults.flat();
          SessionHelper.setCustomersUsers(JSON.stringify(allCustomerUsers));
          this.customersUsers.next(allCustomerUsers);
        },
        (error) => console.error("Error loading customer users:", error)
      );
    }
  }
  //Update project
  updateProject(project: Project) {
    let projects = this.projectList.getValue();
    //console.log('projects count ',projects.length);
    projects[projects.findIndex((x) => x.projectId == project.projectId)] =
      project;
    sessionStorage.setItem("projects", JSON.stringify(projects));
    this.projectList.next(projects);
  }

  //function to clear the datastore
  clearData() {
    this.projectList.next([]);
    this.taskList.next([]);
    this.lightTaskList.next([]);
    this.projectpersonList.next([]);
    this.orgMembers.next([]);
    this.customers.next([]);
    this.customersUsers.next([]);
    this.roles.next([]);
    this.notifications.next([]);
    this.tags.next([]);
    this.filtertagList.next([]);
    this.lastgetdata = new Date().toISOString();
    this.isFirstrun = true;
  }

  // get the ticker data from dynamodb
  getTickerData() {
    // Use UTC time directly - toISOString() already returns UTC
    const now = new Date();
    
    // Use larger buffers in production due to network latency
    const isProduction = environment.production;
    const initialBuffer = isProduction ? 30000 : 10000; // 30s prod, 10s dev
    const ongoingBuffer = isProduction ? 5000 : 1000;   // 5s prod, 1s dev
    
    var oldtime = this.lastgetdata
      ? this.lastgetdata
      : new Date(now.getTime() - initialBuffer).toISOString();
    
    // Set next query time with environment-appropriate buffer
    this.lastgetdata = new Date(now.getTime() - ongoingBuffer).toISOString();
    
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
    return this.http.get<Tick[]>(
      environment.apiUrl +
        "/organization/" +
        SessionHelper.getChoosenOrg() +
        "/ticks/" +
        oldtime,
      httpOptions
    );
  }
  ngOnDestroy() {
    this.uievent.removeLoader("datastore");
    this.uievent.removeLoader("project");
    this.uievent.removeLoader("sortprojectslist");
    this.uievent.removeLoader("loadcustomerusers");
    this.uievent.removeLoader("loadprojectdependentdata");
    clearTimeout(this.loaddatatimeout);
  }

  // Published Forms getter/setter methods
  getPublishedForms(): FormTemplate[] {
    return this.publishedForms.value || [];
  }

  setPublishedForms(forms: FormTemplate[]): void {
    this.publishedForms.next(forms);
  }

  // Load published forms from API
  loadPublishedForms(): Observable<FormTemplate[]> {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      withCredentials: true,
    };
    
    return this.http.get<FormTemplate[]>(
      `${environment.apiUrl}/organization/${SessionHelper.getChoosenOrg()}/forms`,
      httpOptions
    ).pipe(
      map(forms => {
        // Filter for published forms only
        const publishedForms = forms.filter(f => f.status === 'published');
        this.setPublishedForms(publishedForms);
        return publishedForms;
      })
    );
  }

  /**
   * Backfill missing fields in task data for backward compatibility
   * Ensures tasks have the required fields for dashboard calculations
   */
  private backfillTaskData(tasks: Task[]): Task[] {
    return tasks.map(task => {
      const taskAny = task as any;

      if (!task.tasktype || task.tasktype === '') {
        task.tasktype = 'task';
      }

      if (!task.responsible || task.responsible === '') {
        task.responsible = task.creator || '';
      }

      // Normalize form-link fields from alternate API shapes.
      if (!task.formTemplateId) {
        task.formTemplateId = taskAny.form_template_id || taskAny.templateId || taskAny.template_id;
      }

      if (!task.formTemplateName) {
        task.formTemplateName = taskAny.form_template_name || taskAny.templateName || taskAny.template_name;
      }

      if (!task.formTemplateVersion && task.formTemplateVersion !== 0) {
        const rawVersion = taskAny.form_template_version
          ?? taskAny.templateVersion
          ?? taskAny.template_version;

        if (rawVersion !== undefined && rawVersion !== null && rawVersion !== '') {
          const parsed = parseInt(String(rawVersion), 10);
          task.formTemplateVersion = Number.isNaN(parsed) ? undefined : parsed;
        }
      }

      // Set completed_date for tasks in Done state if not already set
      if (task.taskstate === 'Done' && !task.completed_date) {
        task.completed_date = new Date().toISOString();
      }
      return task;
    });
  }

  /**
   * Backfill missing fields in project data for backward compatibility
   * Ensures projects have the required fields for dashboard calculations
   */
  private backfillProjectData(projects: Project[]): Project[] {
    return projects.map(project => {
      // Set workload_fte default if not present
      if (project.workload_fte === undefined) {
        project.workload_fte = 1.0;
      }
      return project;
    });
  }

  /**
   * Backfill missing fields in person/organization data for backward compatibility
   * Ensures persons have the required fields for dashboard calculations
   */
  private backfillPersonData(persons: PersonOrganization[]): PersonOrganization[] {
    return persons.map(person => {
      // Set fte default if not present
      if ((person as any).fte === undefined) {
        (person as any).fte = 1.0;
      }
      // Set isActive to true by default (assume all loaded persons are active)
      if ((person as any).isActive === undefined) {
        (person as any).isActive = true;
      }
      return person;
    });
  }
}

